import { Stack, Theme, Typography, TypographyProps } from "@mui/material";
import { SxProps } from "@mui/system";
import React, { Children, ElementType, forwardRef, PropsWithChildren } from "react";
import { MdsIcon, MdsIconProps } from "../../MdsIcon/MdsIcon";

export interface IconTypographyProps extends PropsWithChildren, TypographyProps {
  /**
   * Icon to add before text
   */
  icon: ElementType;
  /**
   * Icon size (same as fontSize for MdsIcon component)
   */
  iconSize?: MdsIconProps["fontSize"];
  /**
   * Icon color, is used in sx attribute, it can be any valid color
   */
  iconColor?: string | ((theme: Theme) => string);
  /**
   * Vertical icon position, "center" centers it against the whole text block, "firstLine" against
   * the first line of text so the icon stays in place when the text wraps
   */
  iconAlign?: "center" | "firstLine";
  /**
   * Spacing between icon and text
   */
  spacing?: number;
  /**
   * Sx applied to container (Stack)
   */
  sx?: SxProps<Theme>;
}

/**
 * Text with icon in front of text
 */
export const IconTypography = forwardRef<HTMLDivElement, IconTypographyProps>(
  ({ icon, iconSize = "small", iconColor, iconAlign = "center", children, spacing = 1, sx, ...props }, ref) => {
    const iconElement = <MdsIcon fontSize={iconSize} icon={icon} sx={{ color: iconColor }} />;

    return (
      <Stack
        ref={ref}
        direction="row"
        spacing={spacing}
        sx={{
          alignItems: iconAlign === "firstLine" ? "flex-start" : "center",
          ...sx,
        }}
      >
        {iconAlign === "firstLine" ? (
          // wrapper is exactly one line box tall (same typography as the text, so the same
          // line-height), the icon is centered in it and overflows it when it is taller
          <Typography
            component="span"
            sx={{
              alignItems: "center",
              display: "flex",
              flexShrink: 0,
              height: "1.5em", // fallback for browsers without the lh unit
              "@supports (height: 1lh)": { height: "1lh" },
            }}
            variant={props.variant}
          >
            {iconElement}
          </Typography>
        ) : (
          iconElement
        )}
        <Typography {...props}>{Children.toArray(children)[0]}</Typography>
        {Children.toArray(children).slice(1)}
      </Stack>
    );
  }
);
IconTypography.displayName = "IconTypography";
